import { lazy, Suspense, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import Navbar from './layout/Navbar'
import Footer from './layout/Footer'
import Hero from './sections/Hero'
import Marquee from './sections/Marquee'
import About from './sections/About'
import Stack from './sections/Stack'
import Industries from './sections/Industries'
import FAQ from './sections/FAQ'
import Contact from './sections/Contact'
import Newsletter from './sections/Newsletter'
import { useAuth } from '../hooks/useAuth'
import { useCompany } from '../hooks/useCompany'

const AuthModal = lazy(() => import('./modals/AuthModal'))
const ConfigModal = lazy(() => import('./modals/ConfigModal'))
const AdminDashboard = lazy(() => import('./modals/AdminDashboard'))
const CompanySettingsModal = lazy(() => import('./modals/CompanySettingsModal'))

export default function LandingPage() {
  const { isAdmin, viewAsCompanyId, companyId } = useAuth()
  const { settings } = useCompany()
  const location = useLocation()
  const [dismissedEarlySignalsMessage, setDismissedEarlySignalsMessage] = useState(false)

  const earlySignalsAccessDenied = location.state?.earlySignalsAccessDenied === true
  const earlySignalsEnabled = settings?.features?.earlySignals !== false
  // Admins / view-as / users who now have ES access should not see a stale denial banner.
  const showEarlySignalsDeniedBanner =
    earlySignalsAccessDenied &&
    !dismissedEarlySignalsMessage &&
    !isAdmin &&
    !viewAsCompanyId &&
    !(companyId && earlySignalsEnabled)

  const contentConfig = settings?.content_config || {}
  const sections = contentConfig.sections || {}

  const isSectionEnabled = (sectionId: string): boolean => {
    return sections[sectionId]?.enabled !== false
  }

  const features = settings?.features || {}
  const isFeatureEnabled = (featureId: string): boolean => {
    return features[featureId] !== false
  }

  return (
    <>
      <Helmet>
        <title>The Simple | Intelligence. Anytime. Anywhere.</title>
        <meta name="description" content="The Simple exists to simplify the complex and automate the simple. Next-generation management consulting backed by AI and human insight." />
      </Helmet>
      <div className="font-sans text-brand-black antialiased bg-white selection:bg-black selection:text-white flex flex-col min-h-screen scroll-smooth">
        <Navbar />
        {showEarlySignalsDeniedBanner && (
          <div
            className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center justify-between gap-4"
            role="alert"
          >
            <p className="text-amber-900 text-sm">
              Early Signals access is not enabled for your account. Contact your admin if you need access.
            </p>
            <button
              type="button"
              onClick={() => setDismissedEarlySignalsMessage(true)}
              className="text-amber-700 hover:text-amber-900 p-1 rounded"
              aria-label="Dismiss"
            >
              <i className="fa-solid fa-times" aria-hidden />
            </button>
          </div>
        )}
        <main className="flex-grow pt-20">
          <Hero />
          <Marquee />
          {isSectionEnabled('about') && <About />}
          {isSectionEnabled('stack') && <Stack />}
          {isSectionEnabled('industries') && <Industries />}
          {isSectionEnabled('faq') && <FAQ />}
          {isSectionEnabled('contact') && <Contact />}
          {isSectionEnabled('newsletter') && isFeatureEnabled('newsletter') && <Newsletter />}
        </main>
        <Footer />

        <Suspense fallback={null}>
          <AuthModal />
          <ConfigModal />
          <AdminDashboard />
          <CompanySettingsModal />
        </Suspense>
      </div>
    </>
  )
}
